/**
 * The room code (2026-10-06, the user's request): before entering a study, the visitor gives
 * a three character code. The same code is the same room, so a group shares one code and
 * meets apart from other groups reviewing the same study. A new code opens an empty room.
 *
 * A floating card in front of the visitor with the world keyboard out beneath it (the physical
 * keyboard on desktop). It remembers the last code used for each study.
 */
import type * as THREE from 'three';
import { ROOM_CODE_LENGTH, normaliseRoomCode, randomRoomCode } from '@clinspace/shared';
import type { CanvasPanel, Region } from '../../dashboards/CanvasPanel.ts';
import {
  PALETTE,
  chip,
  fillRoundRect,
  font,
  panelBackground,
  text,
} from '../../dashboards/charts.ts';
import { loadItem, saveItem } from '../../lib/storage.ts';
import { FloatingPanel, type FloatingPanelSounds } from '../../queries/FloatingPanel.ts';
import type { InteractiveSystem } from '../../ui/Interactive.ts';

type CodeRegion = { action: 'enter' | 'new' | 'cancel' | 'field' };

const LAST_CODES = 'roomCodes';

/** The code last used for each study on this device. */
export function lastRoomCode(study: string): string | undefined {
  return loadItem<Record<string, string>>(LAST_CODES, {})[study];
}

export function rememberRoomCode(study: string, code: string): void {
  saveItem(LAST_CODES, { ...loadItem<Record<string, string>>(LAST_CODES, {}), [study]: code });
}

export class RoomCodePanel extends FloatingPanel {
  study: { code: string; title: string; colour: string } | undefined;
  /** Why the code cannot be used yet, shown under the boxes. */
  private hint = '';

  constructor(
    interactive: InteractiveSystem,
    private readonly handlers: {
      enter(study: string, code: string): void;
      cancel(): void;
    },
    sounds?: FloatingPanelSounds,
  ) {
    super(
      interactive,
      { width: 0.9, height: 0.5, pixelWidth: 900, pixelHeight: 500, name: 'room-code' },
      sounds,
    );
    this.panel.mesh.renderOrder = 62;
  }

  /** Opens for a study in front of the viewer, the keyboard out, the last code filled in. */
  open(
    study: { code: string; title: string; colour: string },
    head: THREE.Vector3,
    forward: THREE.Vector3,
  ): void {
    this.study = study;
    this.hint = '';
    this.field.clear();
    const last = lastRoomCode(study.code);
    if (last) this.field.value = last;
    const dir = forward.clone().setY(0);
    if (dir.lengthSq() < 1e-6) dir.set(0, 0, -1);
    dir.normalize();
    const at = head.clone().addScaledVector(dir, 0.95);
    this.group.position.copy(at);
    this.group.position.y = head.y - 0.12;
    this.group.rotation.set(-0.12, Math.atan2(-dir.x, -dir.z), 0, 'YXZ');
    this.show();
    this.setTyping(true);
  }

  get code(): string {
    return this.field.value;
  }

  /** Dev hooks and the smoke test: type a code and enter. */
  submitCode(code: string): boolean {
    this.field.value = code;
    return this.confirm();
  }

  protected override onInput(ch: string): void {
    const c = ch.toUpperCase();
    if (!/^[A-Z0-9]$/.test(c) || this.field.value.length >= ROOM_CODE_LENGTH) return;
    this.field.input(c);
    this.hint = '';
    this.panel.invalidate();
  }

  protected onEnter(): void {
    this.confirm();
  }

  protected onEscape(): void {
    this.cancel();
  }

  private confirm(): boolean {
    const code = normaliseRoomCode(this.field.value);
    if (!this.study) return false;
    if (!code) {
      this.hint = `Type ${ROOM_CODE_LENGTH} letters or digits, or press New code.`;
      this.panel.invalidate();
      return false;
    }
    rememberRoomCode(this.study.code, code);
    const study = this.study.code;
    this.hide();
    this.handlers.enter(study, code);
    return true;
  }

  private cancel(): void {
    this.hide();
    this.handlers.cancel();
  }

  protected select(region: Region): void {
    const action = (region.data as CodeRegion | undefined)?.action;
    if (!action) return;
    this.sounds?.click();
    if (action === 'enter') this.confirm();
    else if (action === 'cancel') this.cancel();
    else if (action === 'new') {
      this.field.value = randomRoomCode();
      this.hint = 'A new room: share this code with your group.';
      this.panel.invalidate();
    } else this.setTyping(true);
  }

  protected draw(ctx: CanvasRenderingContext2D, panel: CanvasPanel): void {
    const W = panel.pxW;
    const H = panel.pxH;
    panelBackground(ctx, W, H, 20);
    const s = this.study;
    if (!s) return;
    ctx.fillStyle = s.colour;
    fillRoundRect(ctx, { x: 28, y: 30, w: 8, h: 54 }, 4, s.colour);
    text(ctx, 'ROOM CODE', 52, 46, { size: 12, weight: 700, colour: PALETTE.accentText });
    text(ctx, s.code, 52, 80, { size: 26, weight: 600, display: true, maxWidth: 300 });
    text(ctx, s.title, 52, 104, { size: 12, colour: PALETTE.muted, maxWidth: W - 104 });
    // Three boxes, one per character, with a caret in the next empty one.
    const box = 96;
    const gap = 22;
    const x0 = (W - (box * ROOM_CODE_LENGTH + gap * (ROOM_CODE_LENGTH - 1))) / 2;
    const y0 = 136;
    const value = this.field.value;
    for (let i = 0; i < ROOM_CODE_LENGTH; i++) {
      const r = { x: x0 + i * (box + gap), y: y0, w: box, h: box + 14 };
      const next = i === value.length && this.typing;
      fillRoundRect(ctx, r, 14, PALETTE.field);
      ctx.strokeStyle = next ? PALETTE.teal : PALETTE.line;
      ctx.lineWidth = next ? 3 : 1.5;
      ctx.beginPath();
      ctx.roundRect(r.x + 1, r.y + 1, r.w - 2, r.h - 2, 14);
      ctx.stroke();
      const ch = value[i];
      if (ch)
        text(ctx, ch, r.x + r.w / 2, r.y + r.h / 2 + 22, {
          size: 52,
          weight: 600,
          display: true,
          align: 'center',
        });
      else if (next) {
        ctx.fillStyle = PALETTE.teal;
        ctx.fillRect(r.x + r.w / 2 - 1.5, r.y + 28, 3, r.h - 56);
      }
    }
    panel.region<CodeRegion>({
      id: 'field',
      x: x0,
      y: y0,
      w: box * ROOM_CODE_LENGTH + gap * (ROOM_CODE_LENGTH - 1),
      h: box + 14,
      data: { action: 'field' },
    });
    const note =
      this.hint ||
      'Everyone who enters this code is in the same room. Another code is a separate room.';
    text(ctx, note, W / 2, y0 + box + 52, {
      size: 12,
      colour: this.hint ? PALETTE.accentText : PALETTE.muted,
      align: 'center',
      maxWidth: W - 60,
    });
    // Buttons: Enter room, New code, Cancel.
    const ready = !!normaliseRoomCode(value);
    ctx.font = font(14, 600);
    const buttons: { id: CodeRegion['action']; label: string; primary?: boolean }[] = [
      { id: 'enter', label: 'Enter room', primary: ready },
      { id: 'new', label: 'New code' },
      { id: 'cancel', label: 'Cancel' },
    ];
    const widths = buttons.map((b) => ctx.measureText(b.label).width + 44);
    let x = (W - widths.reduce((a, b) => a + b, 0) - 14 * (buttons.length - 1)) / 2;
    buttons.forEach((b, i) => {
      const id = `action:${b.id}`;
      const r = chip(ctx, b.label, x, H - 76, {
        active: b.primary,
        hovered: panel.isHovered(id),
        h: 46,
        size: 14,
        minW: widths[i],
      });
      panel.region<CodeRegion>({ id, ...r, data: { action: b.id } });
      x += r.w + 14;
    });
  }
}
